<template>
  <div class="bedrooms-form-wrapper">
    <div v-if="this.activePromotion.id" class="bedrooms-form-promotions-wrapper">
      <Pill :label="$t('STAY_BROWSE_ROOMS_PROMO_APPLIED_LABEL', { promo: activePromotion.name })" @remove="clearActivePromotion" />
    </div>
    <div class="bedrooms-form" v-on-clickaway="dismissMenu">
      <BedroomsLocationMenu
        :locations="locations"
        :isOpen="isLocationMenuOpen"
        :selectedLocation="location"
        :userIsMember="userIsMember"
        :promotionLocations="venuesInPromotion"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleLocationMenu"
        @itemSelected="handleLocationSelection"
      />

      <BedroomsDatesMenu
        :selectedDates="datesLabel"
        :isOpen="isDatesMenuOpen"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleDatesMenu"
        @datesSelected="handleDateSelection"
      />

      <BedroomsGuestsMenu
        :selectedGuests="guests"
        :isOpen="isGuestsMenuOpen"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleGuestsMenu"
        @change="handleGuestSelection"
        :isLast="!userIsMember"
      />

      <BedroomsBookingForMenu
        v-if="userIsMember"
        :bookingTypes="availableBookingForOptions"
        :selectedBookingType="bookingFor"
        :isOpen="isBookingForMenuOpen"
        class="bedrooms-form__menu"
        @itemSelected="handleBookingTypeSelection"
        @menuToggle="handleToggleBookingForMenu"
      />

      <div class="bedrooms-form__button-wrapper">
        <ShButton
          :disabled="!canSubmit"
          :aria-disabled="!canSubmit"
          :fullWidth="!$device.isDesktop"
          @click="onSubmit"
        >
          {{ $t("STAY_BROWSE_ROOMS_HEADER") }}
        </ShButton>
      </div>

      <ConfirmationModal
        name="redirect-modal"
        :title="$t('STAY_NO_BOOKING_HEADER')"
        :cancel="$t('STAY_NO_BOOKING_CLOSE_CTA')"
        :confirm="$t('STAY_NO_BOOKING_PUBLIC_CTA')"
        :to="roomBookingUrl"
        @cancel="onRedirectCancel"
      >
        {{ $t('STAY_NO_BOOKING_SUPPORTING') }}
      </ConfirmationModal>

      <ConfirmationModal
        name="enquiry-modal"
        :title="$t('STAY_VETTED_BOOKING_HEADER')"
        :cancel="$t('STAY_VETTED_BOOKING_CLOSE_CTA')"
        :confirm="$t('STAY_VETTED_BOOKING_CTA')"
        @confirm="onEnquiryConfirm"
        @cancel="onEnquiryCancel"
      >
        {{ $t('STAY_VETTED_BOOKING_SUPPORTING') }}
      </ConfirmationModal>

      <Modal
        name="friends-family-modal"
      >
        <div class="bedrooms-form__member-required-modal-text">
          {{
            $t('STAY_MEMBER_REQUIRED_TEXT',
            { location: location.name })
          }}
        </div>
        <template #footer>
          <ShButton @click="$ui.modal.close('friends-family-modal')">
            {{ $t('STAY_MEMBER_REQUIRED_CTA')}}
          </ShButton>
        </template>
      </Modal>
    </div>
  </div>
</template>

<script>
import {
  MenuLabel,
  Pill,
  ShButton,
  ShIcon
} from '@sohohouse/design-system'
import { decorators } from '@sohohouse/platform-resources'
import { mixin as clickaway } from 'vue-clickaway'
import { createNamespacedHelpers, mapGetters } from 'vuex'
import BedroomsLocationMenu from '~/components/sitecore/bedroom-search-form/menus/locations'
import BedroomsDatesMenu from '~/components/sitecore/bedroom-search-form/menus/dates'
import BedroomsGuestsMenu from '~/components/sitecore/bedroom-search-form/menus/guests'
import BedroomsBookingForMenu from '~/components/sitecore/bedroom-search-form/menus/bookingFor'
import { getBedroomVenuesByRegion, generateSynxisBookingURL } from '~/helpers'
import { isStringTruthy } from '~/utils/environment'
import UIMixin from '~/mixins/ui'
import { bookingForMenu, menuConstants, bookingConstants } from './menuConfig'

const { shared: { DateTime } } = decorators

const { mapGetters: mapMembershipGetters } = createNamespacedHelpers('user')
const { mapGetters: mapVenueGetters } = createNamespacedHelpers('venues')
const { mapActions: mapPromotionActions, mapGetters: mapPromotionGetters } = createNamespacedHelpers('promotions')

const MEMBERS_ONLY_HOUSES = ['FARM', 'SHW']
const MEMBER_REQUIRED_HOUSES = ['FARM']

export default {
  name: 'BedroomSearchForm',
  components: {
    Pill,
    MenuLabel,
    ShButton,
    BedroomsLocationMenu,
    BedroomsDatesMenu,
    BedroomsGuestsMenu,
    BedroomsBookingForMenu,
    ShIcon
  },

  mixins: [
    UIMixin,
    clickaway
  ],

  props: {
    emitSearch: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      visibleMenu: ''
    }
  },

  mounted () {
    // TODO: Extend this logic to
    // public users when promotions API
    // is available to public users.
    // This request should also be moved
    // to the fetch method, though
    // it currently doesn't seem to work correctly.
    if (this.userIsMember && this.$route.query.promo) {
      this.requestRoomPromotions()
    }
  },

  watch: {
    '$route.query.bookingForOthers': {
      immediate: true,
      handler: 'handleFriendsFamilyModal'
    }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapMembershipGetters(['membershipType']),
    ...mapVenueGetters(['venuesWithRooms']),
    ...mapPromotionGetters({ promotionById: 'resourceById', promotions: 'resources' }),

    roomBookingUrl () {
      // This is because new URL in generateSynxisBookingURL isn't available on server side
      if (this.$isServer) return ''
      const { location, dates, guests, bookingFor } = this

      const { hotel: { roomBookingUrl } = {} } = this.location
      return roomBookingUrl ? generateSynxisBookingURL(roomBookingUrl, {
        location,
        dates,
        guests,
        bookingFor
      }) : ''
    },

    locations () {
      return getBedroomVenuesByRegion(this.venuesWithRooms)
    },

    canSubmit () {
      const {
        location: {
          id: locationId
        },
        dates: {
          fromDate,
          toDate
        },
        guests: {
          adults
        },
        bookingFor: {
          code: bookingForCode
        }
      } = this

      if (this.userIsMember) {
        return Boolean(
          locationId && adults && fromDate && toDate && bookingForCode
        )
      }

      return Boolean(
        locationId && adults && fromDate && toDate
      )
    },

    isLocationMenuOpen () {
      return this.visibleMenu === menuConstants.LOCATION_MENU
    },

    isDatesMenuOpen () {
      return this.visibleMenu === menuConstants.DATES_MENU
    },

    isGuestsMenuOpen () {
      return this.visibleMenu === menuConstants.GUESTS_MENU
    },

    isBookingForMenuOpen () {
      return this.visibleMenu === menuConstants.BOOKING_FOR_MENU
    },

    availableBookingForOptions () {
      const {
        hotel: { friendsAndFamily: isFriendsAndFamilyBookingEnabled } = {}
      } = this.location
      return bookingForMenu(isFriendsAndFamilyBookingEnabled)
    },

    userIsMember () {
      return Boolean(this.membershipType)
    },

    datesLabel () {
      const { fromDate, toDate } = this.dates
      // Don't show the label
      // unless both are in the query
      if (!fromDate || !toDate || fromDate === toDate) return ''
      return `${new DateTime(fromDate).dateObj.toFormat('dd LLL')} - ${new DateTime(toDate).dateObj.toFormat('dd LLL yyyy')}`
    },

    location () {
      const { location } = this.$route.query
      if (!location) return {}

      // Guard against members-only location
      // being selected by guest
      // via query string
      if (!this.currentUser && MEMBERS_ONLY_HOUSES.includes(location)) return {}

      // Guard against location not in promo
      // being selected via query string
      if (this.activePromotion.id && !this.venuesInPromotion.includes(location)) return {}
      return this.venuesWithRooms.find(venue => venue.id === location) || {
        code: ''
      }
    },

    dates () {
      const { toDate = '', fromDate = '' } = this.$route.query
      return {
        fromDate,
        toDate
      }
    },

    guests () {
      const { adults, children } = this.$route.query

      // Return "default" value if
      // neither is set in URL
      if (!adults && !children) {
        return {
          adults: 2,
          children: 0
        }
      }

      return {
        adults: adults ? Number(adults) : 2,
        children: children ? Number(children) : 0
      }
    },

    bookingFor () {
      const { bookingForOthers } = this.$route.query
      if (!this.userIsMember || bookingForOthers === undefined) return {}

      const { FRIENDS_AND_FAMILY_VALUE, MYSELF_VALUE } = bookingConstants
      return this.availableBookingForOptions.find(({ value }) => isStringTruthy(bookingForOthers)
        ? value === FRIENDS_AND_FAMILY_VALUE
        : value === MYSELF_VALUE)
    },

    activePromotion () {
      const { promo } = this.$route.query
      // Only get active promotion
      // if promotions have been loaded
      // in the store
      if (this.promotions && this.promotions.length) {
        return this.promotionById(promo) || {}
      }
      return {}
    },

    venuesInPromotion () {
      if (!this.activePromotion || !this.activePromotion.hotels) return []
      return this.activePromotion.hotels.map(hotel => hotel.id)
    }
  },

  methods: {
    ...mapPromotionActions(['fetchPromotions']),

    requestRoomPromotions () {
      // Skips if promotions are in store already
      if (!this.promotions || !this.promotions.length) {
        this.fetchPromotions()
      }
    },

    clearActivePromotion () {
      this.updateQueryString({
        promo: undefined
      })
    },

    handleToggleLocationMenu () {
      return this.handleToggleMenu(menuConstants.LOCATION_MENU)
    },

    handleToggleDatesMenu () {
      return this.handleToggleMenu(menuConstants.DATES_MENU)
    },

    handleToggleGuestsMenu () {
      return this.handleToggleMenu(menuConstants.GUESTS_MENU)
    },

    handleToggleBookingForMenu () {
      return this.handleToggleMenu(menuConstants.BOOKING_FOR_MENU)
    },

    handleToggleMenu (menu) {
      this.visibleMenu = menu !== this.visibleMenu ? menu : ''
    },

    handleLocationSelection (location) {
      this.updateQueryString({
        location: location.id,
        bookingForOthers: undefined
      })
      if (!this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    handleBookingTypeSelection (selection) {
      this.updateQueryString({
        bookingForOthers: selection.value === bookingConstants.FRIENDS_AND_FAMILY_VALUE
      })
      if (!this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    handleGuestSelection (selection) {
      this.updateQueryString(selection)
    },

    handleDateSelection (dates) {
      this.updateQueryString({
        fromDate: dates.fromDate,
        toDate: dates.toDate
      })

      if (dates.toDate !== dates.fromDate && !this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    updateQueryString (params) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...params
        }
      })
    },

    dismissMenu () {
      this.visibleMenu = ''
    },

    onRedirectCancel () {
      this.$ui.modal.close('redirect-modal')
    },

    onEnquiryConfirm () {
      this.$ui.modal.close('enquiry-modal')
      this.$router.push(this.localePath({ name: 'contact', query: this.generateEnquiryQuery() }))
    },

    onEnquiryCancel () {
      this.$ui.modal.close('enquiry-modal')
    },

    handleFriendsFamilyModal () {
      const { bookingForOthers } = this.$route.query
      // Restrict the logic to
      // the main bedrooms landing page
      // and not the RLP.
      // This prevents the modal from
      // reappearing when submitting the search.
      // May need to be changed based on product requirements.
      if (this.$route.path.includes('search') || !bookingForOthers) return
      if (MEMBER_REQUIRED_HOUSES.includes(this.location.id)) {
        this.$ui.modal.open('friends-family-modal')
      }
    },

    generateEnquiryQuery () {
      return {
        type: 'bedrooms_enquiry',
        subType: 'enquiry',
        venue: this.location.name,
        textId: `${this.$t('STAY_VETTED_BOOKING_ENQUIRY_TEXT', { dates: this.datesLabel })}`
      }
    },

    onSubmit () {
      if (!this.roomBookingUrl) {
        if (this.emitSearch) {
          this.dismissMenu()
          return this.$emit('search')
        }
        return this.$router.push(this.localePath({
          name: 'bedrooms-search',
          query: this.$route.query
        }))
      }

      if (!this.userIsMember
        && this.location.hotel.publicAccess === bookingConstants.VETTED_HOUSE_VALUE) {
        return this.$ui.modal.open('enquiry-modal')
      }
      return this.$ui.modal.open('redirect-modal')
    }
  }
}
</script>

<style lang="scss" scoped>
$bedroom-form-tablet-width: 425px;
$bedroom-form-max-width: 1440px;

.bedrooms-form-wrapper {
  background-color: $color-white;
  margin-bottom: $space-070;
  padding: 0 $space-050;

  @include desktop {
    margin-bottom: $space-090;
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0 $space-060;
  }
}

.bedrooms-form {
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;

  @include tablet {
    max-width: $bedroom-form-tablet-width;
    margin: 0 auto;
  }

  @include desktop {
    flex-direction: row;
    justify-content: center;
    margin: initial;
    max-width: $bedroom-form-max-width;
  }

  &__menu {
    width: 100%;
    position: relative;

    @include desktop {
      // Ensures label and menu line up
      &:not(:last-child) {
        margin-right: -1px;
      }
    }
  }

  &__button-wrapper {
    margin-top: $space-080;
    width: 100%;

    @include desktop {
      margin-top: 0;
      margin-left: $space-040;
      width: initial;
    }
  }

  &__member-required-modal-text {
    text-align: center;
  }
}

.bedrooms-form-promotions-wrapper {
  padding: $space-050 0;

  @include tablet {
    max-width: $bedroom-form-tablet-width;
    margin: 0 auto;
  }

  @include desktop {
    max-width: unset;
    margin: 0;
  }


}
</style>
